import { useState, useEffect, useRef, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import { useApiClient } from '../../utils/apiClient';
import { responseToBlob } from '../../utils/pdfUtils';

const transformSigningInputsToApiFormat = (meta, pdfDimensions = null) => {
  if (!meta?.signing_inputs || !Array.isArray(meta.signing_inputs)) {
    return null;
  }

  const textBlocks = [];
  let signaturePlacement = null;

  if (!pdfDimensions || !pdfDimensions.pageCount) {
    return null;
  }

  const PDF_WIDTH = pdfDimensions.width;
  const SINGLE_PAGE_HEIGHT = pdfDimensions.height;
  const PAGE_COUNT = pdfDimensions.pageCount;

  meta.signing_inputs.forEach((input) => {
    const { input_code, display_name, input_type, placement_coords, validator_list } = input;

    if (input_type === 'signature') {
      if (placement_coords && placement_coords.length > 0) {
        const coord = placement_coords[0];
        const x = parseInt(coord.x, 10);
        const y = parseInt(coord.y, 10);
        const calculatedPage = Math.floor(y / SINGLE_PAGE_HEIGHT) + 1;
        const pageNumber = Math.min(Math.max(calculatedPage, 1), PAGE_COUNT);
        const yWithinPage = y % SINGLE_PAGE_HEIGHT;
        const xPct = x / PDF_WIDTH;
        const yPct = 1 - (yWithinPage / SINGLE_PAGE_HEIGHT);

        signaturePlacement = {
          xPct,
          yPct,
          pageNumber: pageNumber,
          widthPct: 0.25,
        };
      }
    } else {
      const placements = (placement_coords || []).map((coord) => {
        const x = parseInt(coord.x, 10);
        const y = parseInt(coord.y, 10);
        const calculatedPage = Math.floor(y / SINGLE_PAGE_HEIGHT) + 1;
        const pageNumber = Math.min(Math.max(calculatedPage, 1), PAGE_COUNT);
        const yWithinPage = y % SINGLE_PAGE_HEIGHT;
        const xPct = x / PDF_WIDTH;
        const yPct = 1 - (yWithinPage / SINGLE_PAGE_HEIGHT);

        return {
          xPct,
          yPct,
          pageNumber: pageNumber,
          widthPct: input_type === 'initial' ? 0.12 : 0.25,
        };
      });

      if (placements.length > 0) {
        textBlocks.push({
          id: input_code,
          blockId: input_code,
          label: display_name,
          type: input_type,
          placeholder: getPlaceholderForType(input_type, display_name),
          widthPct: input_type === 'initial' ? 0.12 : 0.25,
          required: validator_list?.includes('required') !== false,
          allowMultiplePlacements: input_type === 'initial' || placements.length > 1,
          placements,
        });
      }
    }
  });

  return {
    defaultTextPlacements: textBlocks,
    defaultPlacements: {
      textBlocks,
      signature: signaturePlacement,
    },
    defaultSignaturePlacement: signaturePlacement,
  };
};

const getPlaceholderForType = (type, displayName) => {
  switch (type) {
    case 'date':
      return 'Select date';
    case 'initial':
      return 'Initials';
    case 'text':
      return `Enter ${displayName.toLowerCase()}`;
    default:
      return 'Enter value';
  }
};

if (typeof window !== 'undefined' && !pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

export const usePDFViewer = (document, isOpen, useSignedPdf) => {
  const { apiGet } = useApiClient();
  
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [pdfDimensions, setPdfDimensions] = useState(null);
  const [docApiResponse, setDocApiResponse] = useState(null);
  const [hasApiResponse, setHasApiResponse] = useState(false);
  
  const pdfLoadAbortControllerRef = useRef(null);
  const lastLoadedDocRef = useRef(null);
  const pdfPageRefs = useRef({});

  useEffect(() => {
    if (typeof window !== 'undefined' && !pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    }
  }, []);

  useEffect(() => {
    pdfPageRefs.current = {};
    lastLoadedDocRef.current = null;
    setPdfDimensions(null);
    setHasApiResponse(false);
    setDocApiResponse(null);
  }, [document?.id]);

  useEffect(() => {
    if (!isOpen) {
      lastLoadedDocRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (document?.meta?.signing_inputs && !hasApiResponse) {
      const transformed = transformSigningInputsToApiFormat(document.meta, pdfDimensions);
      if (transformed) {
        setDocApiResponse(transformed);
      }
    }
  }, [document?.meta, pdfDimensions, hasApiResponse]);

  const buildEndpointUrl = useCallback((documentId, useSignedPdf) => {
    const docsignParams = {
      document_id: String(documentId),
    };
    const qs = new URLSearchParams(docsignParams).toString();

    return useSignedPdf
      ? `/docsign/signed_doc_content?${qs}`
      : `/docsign/doc_content?${qs}`;
  }, []);

  const loadPdfFromEndpoint = useCallback(async (endpoint, isCancelled, abortController) => {
    try {
      const response = await apiGet(endpoint);
      
      if (isCancelled || abortController?.signal?.aborted) {
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
      }
      
      const { blob, jsonResponse } = await responseToBlob(response);
      
      if (isCancelled || abortController?.signal?.aborted) {
        return null;
      }
      
      if (!blob || blob.size === 0) {
        throw new Error('Failed to create PDF blob: empty or invalid blob');
      }
      
      const url = URL.createObjectURL(blob);
      
      return { url, jsonResponse };
    } catch (error) {
      console.error('Error loading PDF from endpoint:', error);
      throw error;
    }
  }, [apiGet]);

  useEffect(() => {
    if (!document || !isOpen || !document.id) return;

    const loadKey = `${document.id}-${useSignedPdf ? 'signed' : 'unsigned'}`;

    if (lastLoadedDocRef.current === loadKey) {
      return;
    }

    if (pdfLoadAbortControllerRef.current) {
      pdfLoadAbortControllerRef.current.abort();
    }

    let currentUrl = null;
    let isCancelled = false;
    const abortController = new AbortController();
    pdfLoadAbortControllerRef.current = abortController;

    const loadPdf = async () => {
      try {
        setIsLoadingPdf(true);
        setError(null);
        setNumPages(0);
        setPdfUrl(null);
        setPdfDimensions(null);

        const endpoint = buildEndpointUrl(document.id, useSignedPdf);
        const result = await loadPdfFromEndpoint(endpoint, isCancelled, abortController);
        
        if (!result) {
          return null;
        }
        
        currentUrl = result.url;
        setPdfUrl(result.url);
        setIsLoadingPdf(false);
        lastLoadedDocRef.current = loadKey;

        if (result.jsonResponse) {
          setDocApiResponse(result.jsonResponse);
          setHasApiResponse(true);
        } else {
          setHasApiResponse(false);
        }

        return result.jsonResponse;
      } catch (error) {
        if (!isCancelled && !abortController.signal.aborted) {
          setError(error.message || `Failed to load PDF for: ${document.name}`);
          setPdfUrl(null);
          setIsLoadingPdf(false);
        }
        throw error;
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      abortController.abort();
      if (pdfLoadAbortControllerRef.current === abortController) {
        pdfLoadAbortControllerRef.current = null;
      }
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [document, isOpen, useSignedPdf, buildEndpointUrl, loadPdfFromEndpoint]);

  const onDocumentLoadSuccess = useCallback(async ({ numPages }) => {
    setNumPages(numPages);
    setError(null);

    try {
      if (pdfUrl) {
        const response = await fetch(pdfUrl);
        const arrayBuffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pages = [];
        let totalHeight = 0;
        let maxWidth = 0;

        for (let i = 0; i < numPages; i++) {
          const page = pdfDoc.getPage(i);
          const { width, height } = page.getSize();
          const rotation = page.getRotation().angle || 0;
          pages.push({ width, height, rotation });
          totalHeight += height;
          maxWidth = Math.max(maxWidth, width);
        }

        setPdfPages(pages);
        setPdfDimensions({
          width: maxWidth,
          height: pages[0]?.height || 792,
          totalHeight: totalHeight,
          pageCount: numPages
        });
      }
    } catch (err) {
    }
  }, [pdfUrl]);

  const onDocumentLoadError = useCallback((error) => {
    setError('Failed to load PDF document');
  }, []);

  const handleRetry = useCallback(async () => {
    if (!document || !document.id) return;

    try {
      setIsLoadingPdf(true);
      setError(null);

      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      setPdfUrl(null);

      const endpoint = buildEndpointUrl(document.id, useSignedPdf);
      const result = await loadPdfFromEndpoint(endpoint, false, null);

      if (result) {
        setPdfUrl(result.url);

        if (result.jsonResponse) {
          setDocApiResponse(result.jsonResponse);
          setHasApiResponse(true);
        } else {
          setHasApiResponse(false);
        }
      }

      setIsLoadingPdf(false);
    } catch (error) {
      setError(error.message || `Failed to load PDF for: ${document.name}`);
      setIsLoadingPdf(false);
    }
  }, [document, useSignedPdf, buildEndpointUrl, loadPdfFromEndpoint, pdfUrl]);

  const registerPageRef = useCallback((pageNumber, node) => {
    if (!node) {
      delete pdfPageRefs.current[pageNumber];
      return;
    }
    pdfPageRefs.current[pageNumber] = node;
  }, []);

  return {
    numPages,
    error,
    pdfUrl,
    isLoadingPdf,
    pdfPages,
    pdfDimensions,
    pdfPageRefs,
    docApiResponse,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    handleRetry,
    registerPageRef,
  };
};
